import React from "react";

import classNames from "classnames";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import { useSearchParams, useParams } from "../../shared/hooks/useNavigation";
import { useRouter } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import styled from "styled-components";

import { useStores } from "../../contexts/StoreContext";
import LocaleSelector from "../../shared/components/LocaleSelector/LocaleSelector";
import useDevice from "../../shared/customHooks/useDevice";
import useFilters from "../../shared/customHooks/useFilters";
import Markdown from "../../shared/styles/Markdown";
import FindYourRole from "../FindYourRole/FindYourRole";
import SocialMediaLinks from "../Footer/SocialMediaLinks";

const StyledMarkdown = styled(Markdown)`
  color: ${({ textColor }) => textColor || "#000"} !important;
  p {
    margin-top: 0;
    margin-bottom: 0;
  }
`;

const StyledBox = styled.div`
  min-width: 25rem;
`;

const StyledMobileSearchBox = styled.div`
  transition: height 0.3s ease, opacity 0.3s ease;
  display: ${({ display }) => (display ? "block" : "none")};
  height: ${({ display }) => (display ? "auto" : "0")};
  background: black;
  position: absolute;
  top: 100px;
  padding: 1rem;
  left: 0;
  width: 100%;
  z-index: 1;
`;

const StyledImageContainer = styled.div`
  width: 30px;
  height: 30px;
  position: relative;
`;

function NavbarActions(props) {
  const {
    navbarText,
    navbarTextColor,
    showLocaleSelector,
    socialMediaSettings,
    showSearch,
    searchBarButtonText,
    searchBarKeywordsText,
    searchBarLocationText,
  } = props;
  const { filtersStore } = useStores();
  const { filters } = filtersStore;
  const [isDisplayed, setIsDisplayed] = React.useState(false);

  const { handleSubmit } = useFilters();
  const params = useParams();
  const searchParams = useSearchParams();
  const search = Object.fromEntries(searchParams.entries());
  const combindedParms = { ...params, ...search };
  const router = useRouter();

  const handleSearch = () => {
    handleSubmit(
      router,
      "/results",
      filters,
      [
        { field: "keywords", placeholder: "Keywords..." },
        { field: "location", placeholder: "Location..." },
      ],
      combindedParms
    );
  };

  const { isMobile, isSmallScreen, isMediumScreen } = useDevice();

  const showSocialMediaNavigation = !isEmpty(socialMediaSettings);
  const socialMediaLinksClassName = classNames(
    "ms-3 d-none d-lg-block",
    socialMediaSettings?.socialMediaWrapperClassName
  );

  if (
    !navbarText &&
    !showLocaleSelector &&
    !showSocialMediaNavigation &&
    !showSearch
  ) {
    return null;
  }

  return (
    <div className="d-flex align-items-center ms-3">
      {navbarText && !isMobile && (
        <StyledMarkdown className="me-5 fw-bolder" textColor={navbarTextColor}>
          {navbarText}
        </StyledMarkdown>
      )}
      {showLocaleSelector && <LocaleSelector />}
      {showSocialMediaNavigation && !isMobile && (
        <SocialMediaLinks
          iconClassName="me-3"
          iconColor={socialMediaSettings?.iconColor}
          iconSize={socialMediaSettings?.iconSize}
          mediaLinksWrapperClassName={socialMediaLinksClassName}
        />
      )}
      {showSearch &&
        (isMobile || isSmallScreen || isMediumScreen ? (
          <>
            <StyledImageContainer onClick={() => setIsDisplayed(!isDisplayed)}>
              <Image
                alt="Search"
                layout="fill"
                objectFit="contain"
                src="/search-job.svg"
              />
            </StyledImageContainer>
            <StyledMobileSearchBox
              className="navbar-mobile-searchbox"
              display={isDisplayed}
            >
              <FindYourRole
                buttonText="Search jobs"
                fieldsConfiguration={[
                  {
                    field: "keywords",
                    placeholder: searchBarKeywordsText || "Keywords...",
                  },
                  {
                    field: "location",
                    placeholder: searchBarLocationText || "Location...",
                  },
                ]}
                isInline={false}
                onSubmit={handleSearch}
                text={null}
                withoutBottomMargin
              />
            </StyledMobileSearchBox>
          </>
        ) : (
          <StyledBox>
            <FindYourRole
              buttonText={searchBarButtonText || "Search jobs"}
              fieldsConfiguration={[
                {
                  field: "keywords",
                  placeholder: searchBarKeywordsText || "Keywords...",
                },
                {
                  field: "location",
                  placeholder: searchBarLocationText || "Location...",
                },
              ]}
              isEvenBoxSizes
              isInline
              onSubmit={handleSearch}
              text={null}
              withoutBottomMargin
            />
          </StyledBox>
        ))}
    </div>
  );
}

export default observer(NavbarActions);

NavbarActions.propTypes = {
  navbarText: PropTypes.string,
  navbarTextColor: PropTypes.string,
  searchBarButtonText: PropTypes.string,
  searchBarKeywordsText: PropTypes.string,
  searchBarLocationText: PropTypes.string,
  showLocaleSelector: PropTypes.bool.isRequired,
  showSearch: PropTypes.bool,
  socialMediaSettings: PropTypes.shape({
    iconColor: PropTypes.string,
    iconSize: PropTypes.string,
    socialMediaWrapperClassName: PropTypes.string,
  }),
};

NavbarActions.defaultProps = {
  navbarText: null,
  navbarTextColor: null,
  searchBarButtonText: null,
  searchBarKeywordsText: null,
  searchBarLocationText: null,
  showSearch: false,
  socialMediaSettings: null,
};
